import { useState, useEffect } from 'react';
import { X, Download, Loader2, FileText } from 'lucide-react';

/**
 * PDF Preview Modal Component
 * Displays a PDF in an iframe with download functionality
 */
const PdfPreviewModal = ({ isOpen, onClose, pdfUrl, certificateNum }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && pdfUrl) {
      setLoading(true);
      setError(null);
    }
  }, [isOpen, pdfUrl]);

  const handleDownload = async () => {
    if (!pdfUrl) {
      console.error('No PDF URL available for download');
      setError('PDF URL not available. Please try exporting again.');
      return;
    }
    
    try {
      console.log('Downloading PDF from URL:', pdfUrl);
      
      // Fetch PDF and trigger download
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `EICR-${certificateNum || 'certificate'}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      console.log('Download triggered successfully');
    } catch (err) {
      console.error('Download error:', err);
      setError(`Download failed: ${err.message}`);
    }
  };

  const handleIframeLoad = () => {
    // Small delay to ensure PDF is fully rendered
    setTimeout(() => {
      setLoading(false);
      console.log('PDF iframe/object loaded successfully');
    }, 500);
  };

  const handleIframeError = (e) => {
    setLoading(false);
    console.error('PDF load error:', e);
    console.error('PDF URL:', pdfUrl?.substring(0, 100));
    console.error('Error type:', e.type);
    console.error('Error target:', e.target);
    
    // Check if it's a blob URL issue
    if (pdfUrl && pdfUrl.startsWith('blob:')) {
      setError('Browser security settings prevent inline PDF preview from blob URLs. Please use the download or "Open in New Tab" button.');
    } else {
      setError('Failed to load PDF preview. The PDF may be corrupted or invalid. Please try downloading instead.');
    }
  };
  
  // Add timeout to detect if PDF fails to load
  useEffect(() => {
    if (isOpen && pdfUrl) {
      setLoading(true);
      setError(null);
      
      const timeout = setTimeout(() => {
        if (loading) {
          setLoading(false);
          // Don't set error immediately - PDF might still be loading
          // Just hide loading indicator
        }
      }, 15000); // 15 second timeout
      
      return () => clearTimeout(timeout);
    }
  }, [isOpen, pdfUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Certificate Preview
          </h2>
          <div className="flex items-center gap-3">
            {pdfUrl && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 text-2xl transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* PDF Preview - Show message since browsers block blob URLs inline */}
        <div className="flex-1 relative bg-gray-100 dark:bg-gray-900 overflow-hidden flex items-center justify-center">
          <div className="text-center p-8 max-w-md">
            {error && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-1">Error:</p>
                <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
            <div className="mb-6">
              <FileText className="h-16 w-16 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                PDF Ready for Download
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Your PDF certificate is ready. Click the buttons below to view in a new tab or download.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-6">
                Certificate Number: {certificateNum || 'N/A'}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDownload}
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Download className="h-5 w-5" />
                Download PDF Certificate
              </button>
              {pdfUrl && (
                <button
                  onClick={() => {
                    try {
                      console.log('Opening PDF in new tab:', pdfUrl);
                      // Use regular HTTP URL - browsers handle these much better than blob URLs
                      // The URL goes through Vite proxy in dev, or directly to backend in prod
                      const newWindow = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                      
                      if (!newWindow || newWindow.closed) {
                        setError('Popup blocked. Please allow popups for this site or use the download button.');
                        return;
                      }
                      
                      // Close modal after opening
                      setTimeout(() => onClose(), 500);
                    } catch (err) {
                      console.error('Error opening PDF:', err);
                      setError(`Could not open PDF: ${err.message}. Please use the download button instead.`);
                    }
                  }}
                  className="w-full px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Open in New Tab
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-3">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>Certificate Number: {certificateNum || 'N/A'}</span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal;

